import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { LucideModule } from '../../../shared/modules/lucide.module';
import {
  CATEGORIA_SERVICOS_TIPOS,
  CategoriaServicosTipo,
} from '../models/categoria-servicos.model';
import {
  CategoriaPublicaItem,
  CategoriasPublicasService,
} from '../services/categorias-publicas.service';
import { PageCategoriaServicosCardComponent } from './page-categoria-servicos-card/page-categoria-servicos-card.component';
import { PaginacaoComponent } from '../../../shared/components/paginacao/paginacao.component';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { InputBuscarComponent } from '../../../shared/components/input-buscar/input-buscar.component';
import { LinksDinamicosComponent } from '../links-dinamicos/links-dinamicos.component';
import { ScrollNavegacaoService } from '../../../core/services/scroll-navegacao.service';

const ACENTOS = /\p{Diacritic}/gu;

const CATEGORIAS_POR_PAGINA = 12;

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

/**
 * A tela "Categorias de Serviços": as categorias que o órgão publicou no adm, com filtro
 * por categoria e por tipo e busca por nome ou descrição — tudo no cliente, sobre a lista
 * inteira que o `CategoriasPublicasService` deriva do catálogo público.
 */
@Component({
  selector: 'app-page-categoria-servicos',
  templateUrl: './page-categoria-servicos.component.html',
  imports: [
    LinksDinamicosComponent,
    InputBuscarComponent,
    BreadcrumbComponent,
    LucideModule,
    PageCategoriaServicosCardComponent,
    PaginacaoComponent,
  ],
  host: {
    '(document:click)': 'aoClicarForaDoSelect($event)',
    '(document:keydown.escape)': 'fecharSelectComFoco()',
  },
})
export class PageCategoriaServicosComponent {
  private readonly categoriasPublicasService = inject(CategoriasPublicasService);
  private readonly injector = inject(Injector);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);

  private readonly listaCategorias = viewChild<ElementRef<HTMLElement>>('listaCategorias');
  private readonly selectCategorias = viewChild<ElementRef<HTMLElement>>('selectCategorias');
  private readonly selectCategoriasControle =
    viewChild<ElementRef<HTMLButtonElement>>('selectCategoriasControle');

  readonly selectCategoriasAberto = signal(false);
  readonly termoBusca = signal('');
  readonly categoriasSelecionadas = signal<ReadonlySet<string>>(new Set());
  readonly tiposSelecionados = signal<ReadonlySet<CategoriaServicosTipo>>(new Set());

  readonly categorias = this.categoriasPublicasService.categorias;
  readonly carregando = this.categoriasPublicasService.carregando;
  readonly falhou = this.categoriasPublicasService.falhou;

  /** O órgão não tem nenhuma categoria publicada — diferente de "nada casa com o filtro". */
  readonly semCatalogo = computed(
    () => !this.carregando() && !this.falhou() && this.categorias().length === 0
  );

  readonly resumoCategorias = computed(() => {
    const selecionadas = this.categorias().filter((categoria) =>
      this.categoriasSelecionadas().has(categoria.id)
    );

    if (selecionadas.length === 0) return 'Todas as categorias';
    if (selecionadas.length === 1) return selecionadas[0].titulo;

    return `${selecionadas.length} categorias selecionadas`;
  });

  readonly totalPorTipo = computed(() => {
    const total = new Map<CategoriaServicosTipo, number>();

    for (const categoria of this.categorias()) {
      for (const tipo of categoria.tipos) {
        total.set(tipo, (total.get(tipo) ?? 0) + 1);
      }
    }

    return total;
  });

  /** Só os tipos que alguma categoria tem — por isso "De cadastro" não aparece. */
  readonly tiposCategoriaServicos = computed(() =>
    CATEGORIA_SERVICOS_TIPOS.filter((opcao) => this.totalPorTipo().has(opcao.tipo))
  );

  readonly categoriasFiltradas = computed<readonly CategoriaPublicaItem[]>(() => {
    const termo = normalizar(this.termoBusca());
    const selecionadas = this.categoriasSelecionadas();
    const tipos = this.tiposSelecionados();

    return this.categorias().filter(
      (categoria) =>
        this.combinaComCategoria(categoria, selecionadas) &&
        this.combinaComTipo(categoria, tipos) &&
        this.combinaComTermo(categoria, termo)
    );
  });

  readonly totalResultados = computed(() => this.categoriasFiltradas().length);

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.totalResultados() / CATEGORIAS_POR_PAGINA))
  );

  readonly paginaAtual = linkedSignal({
    source: () => ({
      termo: this.termoBusca(),
      categorias: this.categoriasSelecionadas(),
      tipos: this.tiposSelecionados(),
    }),
    computation: () => 1,
  });

  readonly categoriasDaPagina = computed<readonly CategoriaPublicaItem[]>(() => {
    const inicio = (this.paginaAtual() - 1) * CATEGORIAS_POR_PAGINA;

    return this.categoriasFiltradas().slice(inicio, inicio + CATEGORIAS_POR_PAGINA);
  });

  readonly temFiltroAtivo = computed(
    () =>
      !!this.termoBusca() ||
      this.categoriasSelecionadas().size > 0 ||
      this.tiposSelecionados().size > 0
  );

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);

    afterNextRender(() => this.rolarParaLista(), { injector: this.injector });
  }

  /** O "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.categoriasPublicasService.recarregar();
  }

  private rolarParaLista(): void {
    this.scrollNavegacao.aoElemento(this.listaCategorias()?.nativeElement);
  }

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);
  }

  limparBusca(): void {
    this.termoBusca.set('');
  }

  alternarCategoria(id: string): void {
    this.categoriasSelecionadas.update((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(id)) {
        proximo.add(id);
      }
      return proximo;
    });
  }

  limparCategorias(): void {
    this.categoriasSelecionadas.set(new Set());
  }

  alternarSelectCategorias(): void {
    this.selectCategoriasAberto.update((aberto) => !aberto);
  }

  fecharSelectComFoco(): void {
    if (!this.selectCategoriasAberto()) return;

    this.selectCategoriasAberto.set(false);
    this.selectCategoriasControle()?.nativeElement.focus();
  }

  aoClicarForaDoSelect(evento: Event): void {
    if (!this.selectCategoriasAberto()) return;

    const alvo = evento.target as Node | null;
    if (alvo && this.selectCategorias()?.nativeElement.contains(alvo)) return;

    this.selectCategoriasAberto.set(false);
  }

  alternarTipo(tipo: CategoriaServicosTipo): void {
    this.tiposSelecionados.update((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(tipo)) {
        proximo.add(tipo);
      }
      return proximo;
    });
  }

  limparTipos(): void {
    this.tiposSelecionados.set(new Set());
  }

  limparTudo(): void {
    this.limparBusca();
    this.limparCategorias();
    this.limparTipos();
  }

  categoriaSelecionada(id: string): boolean {
    return this.categoriasSelecionadas().has(id);
  }

  /**
   * O nome acessível da caixa de seleção. Explícito porque o texto do `<label>` junta o nome
   * e o total sem separador ("Saúde3") — o template não preserva o espaço entre os spans.
   */
  rotuloOpcaoCategoria(categoria: CategoriaPublicaItem): string {
    const unidade = categoria.totalServicos === 1 ? 'serviço' : 'serviços';

    return `${categoria.titulo}, ${categoria.totalServicos} ${unidade}`;
  }

  tipoSelecionado(tipo: CategoriaServicosTipo): boolean {
    return this.tiposSelecionados().has(tipo);
  }

  totalDoTipo(tipo: CategoriaServicosTipo): number {
    return this.totalPorTipo().get(tipo) ?? 0;
  }

  private combinaComCategoria(
    categoria: CategoriaPublicaItem,
    selecionadas: ReadonlySet<string>
  ): boolean {
    return selecionadas.size === 0 || selecionadas.has(categoria.id);
  }

  private combinaComTipo(
    categoria: CategoriaPublicaItem,
    tipos: ReadonlySet<CategoriaServicosTipo>
  ): boolean {
    return tipos.size === 0 || categoria.tipos.some((tipo) => tipos.has(tipo));
  }

  /** Nome e descrição da categoria — o nome dos serviços dela não entra (decisão de 29/09/2026). */
  private combinaComTermo(categoria: CategoriaPublicaItem, termo: string): boolean {
    if (!termo) return true;

    const alvo = normalizar(`${categoria.titulo} ${categoria.descricao}`);

    return alvo.includes(termo);
  }
}
